import { PlaylistRoomAudiosList } from 'app/components/Playlist/PlaylistRoomAudiosList';
import { ChakraFabButton, ContentContainer } from 'app/components/Core';
import { ReactComponent as AddIcon } from 'resources/images/add.svg';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { PlaylistHeaderLoader } from 'app/components/Playlist/PlaylistHeaderLoader';
import {
  getPatchPlaylistLink,
  getPlaylist,
  getPlaylistAddingAudiosLoadingState,
} from 'app/store/Playlists/selectors';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { routes } from 'app/routing/routesConfig';
import {
  addPlaylistAudiosEffect,
  deletePlaylistAudioEffect,
  fetchPlaylistEffect,
  patchPlaylistEffect,
} from 'app/store/Playlists/effects';
import { isLoading } from 'app/utils/utils';
import { PlaylistRoomHeader } from 'app/components/Playlist/PlaylistRoomHeader';
import { useDisclosure } from '@chakra-ui/react';
import { PlaylistAudioSelectOverlay } from 'app/components/Playlist/PlaylistAudioSelectOverlay';
import { SelectedAudioObject } from 'app/components/Audio';
import { addAlertAction } from 'app/store/Alerts/slice';
import { useFetchValidationSchema } from 'app/utils/hooks';
import { PlaylistValidations, UpdatePlaylistData } from 'types/playlists.types';
import {
  useAudioPlayer,
  usePlayerPlaylist,
} from 'app/components/AudioPlayer/hooks';
import { usePlaylistFeatureFlag } from 'featureFlags';
import { addAudioListToQueueAction } from 'app/store/AudioPlayer/slice';
import { ContentContainerType } from 'types/enums';
import { PlaylistRoomDescription } from 'app/components/Playlist/PlaylistRoomDescription';

export const Playlist = () => {
  const { playlistFeatureEnabled } = usePlaylistFeatureFlag();
  const dispatch = useAppDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const shouldNotFetch = state?.shouldNotFetch;
  const guid = params.guid || '';
  const [playlistLoading, setPlaylistLoading] = useState(!shouldNotFetch);
  const addingAudiosLoadingState = useAppSelector(
    getPlaylistAddingAudiosLoadingState
  );
  const playlist = useAppSelector(getPlaylist);
  const patchPlaylistLink = useAppSelector(getPatchPlaylistLink);
  const addAudiosOverlay = useDisclosure();
  const { stop } = useAudioPlayer();

  const fetchPlaylist = async () => {
    try {
      await dispatch(fetchPlaylistEffect({ playlistGuid: guid })).unwrap();
    } catch (e) {
      navigate(routes.playlists.path);
    } finally {
      setPlaylistLoading(false);
    }
  };

  const onRemoveAudio = async (audioGuid: string) => {
    await dispatch(deletePlaylistAudioEffect({ audioGuid })).unwrap();
    if (playlist?.playlistAudios.length === 1) {
      stop();
    }
  };

  const updatePlaylist = async (data: UpdatePlaylistData) => {
    await dispatch(patchPlaylistEffect(data)).unwrap();
  };

  useEffect(() => {
    if (shouldNotFetch) {
      navigate('', { state: { shouldNotFetch: undefined } });
    } else {
      fetchPlaylist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guid]);

  const loading = isLoading(addingAudiosLoadingState) || playlistLoading;

  const handleAddAudios = async (audios: SelectedAudioObject) => {
    try {
      const data = await dispatch(
        addPlaylistAudiosEffect({
          audios,
          playlistGuid: guid,
          context: 'PLAYLIST_ROOM',
        })
      ).unwrap();
      if (data) {
        dispatch(
          addAudioListToQueueAction(
            data.map((el) => ({
              audioGuid: el.audio.guid,
              playlistAudioGuid: el.guid,
              parentGuid: el.song.guid,
              songTitle: el.song.title,
              audioTitle: el.audio.title,
              note: el.note,
              duration: el.audio.duration,
            }))
          )
        );
      }
      addAudiosOverlay.onClose();
      dispatch(
        addAlertAction({
          message:
            Object.keys(audios).length > 1 ? 'Audios added' : 'Audio added',
        })
      );
    } catch (e) {
      // proceed
    }
  };

  const {
    validationSchema,
    fetchingValidations,
  } = useFetchValidationSchema<PlaylistValidations>({
    link: patchPlaylistLink,
  });

  const { loadPlaylist } = usePlayerPlaylist();

  const audios = useMemo(
    () => (playlist?.playlistAudios || []).map(({ audio }) => audio),
    [playlist?.playlistAudios]
  );

  if (!playlistFeatureEnabled) {
    return <Navigate to="/" />;
  }

  const loadPlaylistAudio = (playIndex: number) =>
    loadPlaylist({
      playlistGuid: playlist?.guid || '',
      playIndex,
      playedLocation: 'PLAYLIST_ROOM_TRACK',
      images: playlist?.images,
    });

  return (
    <>
      {playlistLoading || !playlist || fetchingValidations ? (
        <PlaylistHeaderLoader />
      ) : (
        <PlaylistRoomHeader
          guid={playlist.guid}
          title={playlist.title}
          images={playlist.images}
          audios={audios}
          loadPlaylistAudio={loadPlaylistAudio}
          validations={validationSchema}
          onUpdate={updatePlaylist}
        />
      )}
      <ContentContainer type={ContentContainerType.LIST} isViewContent pt={0}>
        {playlist && (
          <PlaylistRoomDescription
            playlistTitle={playlist?.title}
            description={playlist.description || ''}
            onUpdate={(description) => updatePlaylist({ description })}
            maxLength={validationSchema?.properties.description.maxLength || 0}
          />
        )}
        <PlaylistRoomAudiosList
          audios={playlist?.playlistAudios || []}
          isLoading={isLoading(addingAudiosLoadingState) || playlistLoading}
          onRemoveAudio={onRemoveAudio}
          loadPlaylist={loadPlaylistAudio}
        />
        <ChakraFabButton
          isDisabled={loading}
          aria-label="add audio"
          onClick={addAudiosOverlay.onOpen}
          leftIcon={<AddIcon />}
          data-testid="add-audio"
        >
          Add Audio
        </ChakraFabButton>
      </ContentContainer>
      {addAudiosOverlay.isOpen && (
        <PlaylistAudioSelectOverlay
          onClose={addAudiosOverlay.onClose}
          onAdd={handleAddAudios}
        />
      )}
    </>
  );
};
